import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { onlineModels } from "@/mocks/models";

export default function HeroSection() {
  const [[activeIndex, direction], setActive] = useState<[number, number]>([0, 0]);

  const goNext = useCallback(() => {
    setActive(([prev]) => [(prev + 1) % onlineModels.length, 1]);
  }, []);

  const goPrev = useCallback(() => {
    setActive(([prev]) => [(prev - 1 + onlineModels.length) % onlineModels.length, -1]);
  }, []);

  const goTo = useCallback((index: number) => {
    setActive(([prev]) => [index, index > prev ? 1 : -1]);
  }, []);

  useEffect(() => {
    const timer = setInterval(goNext, 4500);
    return () => clearInterval(timer);
  }, [goNext]);

  const model = onlineModels[activeIndex];

  const slideVariants = {
    enter: (dir: number) => ({ x: dir > 0 ? 120 : -120, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir: number) => ({ x: dir < 0 ? 120 : -120, opacity: 0 }),
  };

  return (
    <section className="px-4 pb-4 pt-3">
      {/* Hero copy */}
      <motion.div
        className="mb-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.25, 0.4, 0.25, 1] }}
      >
        {/* <h1 className="mb-2 text-2xl font-bold leading-tight text-foreground-50 md:text-3xl">
          今夜だけの出会い？
          <br />
          <span className="text-primary-400">プライベートライブに今すぐ参加！</span>
        </h1> */}
        <p className="text-sm leading-relaxed text-foreground-400 md:text-base">
          日本中のイケてる女性たちがライブ配信中。無修正のビデオチャットで、あなたの隠れた欲望を叶えてくれる。アプリをダウンロードして無料パスをGET。
        </p>
      </motion.div>

      {/* Animated carousel */}
      <div className="relative overflow-hidden rounded-2xl">
        <AnimatePresence initial={false} custom={direction} mode="wait">
          <motion.div
            key={activeIndex}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ type: "spring", stiffness: 280, damping: 28 }}
            className="relative"
          >
            <div className="relative aspect-[3/4] w-full overflow-hidden rounded-2xl">
              <img
                src={model.image}
                alt={`${model.name} ${model.age}歳`}
                className="h-full w-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

              {/* Verified badge */}
              {model.isVerified && (
                <motion.div
                  className="absolute left-3 top-3 flex items-center gap-1 rounded-full bg-blue-500/90 px-2.5 py-1 text-xs font-bold text-white backdrop-blur-sm"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.3, type: "spring", stiffness: 400 }}
                >
                  <i className="ri-checkbox-circle-fill" />
                  本人確認済み
                </motion.div>
              )}

              {/* Live badge */}
              {model.isLive && (
                <motion.div
                  className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-primary-500/90 px-2.5 py-1 text-xs font-bold text-white backdrop-blur-sm"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.35, type: "spring", stiffness: 400 }}
                >
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
                  </span>
                  ライブ配信中
                </motion.div>
              )}

              {/* Info */}
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <motion.div
                  className="mb-1 text-lg font-bold text-white"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.3 }}
                >
                  {model.name}、{model.age}歳
                </motion.div>
                <motion.div
                  className="mb-2 flex items-center gap-1 text-sm text-white/80"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.45, duration: 0.3 }}
                >
                  <i className="ri-map-pin-line" />
                  {model.distance} · {model.location}
                </motion.div>
                <motion.div
                  className="flex flex-wrap gap-1.5"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.3 }}
                >
                  {model.tags.map((tag, i) => (
                    <span
                      key={i}
                      className={`rounded-full px-2.5 py-1 text-xs font-medium backdrop-blur-sm ${
                        i === 0
                          ? "bg-primary-500/90 text-white"
                          : "bg-white/20 text-white/90"
                      }`}
                    >
                      {tag}
                    </span>
                  ))}
                </motion.div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Dots */}
        <div className="mt-3 flex justify-center gap-2">
          {onlineModels.map((_, index) => (
            <button
              key={index}
              onClick={() => goTo(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === activeIndex
                  ? "w-6 bg-primary-500"
                  : "w-2 bg-background-400 hover:bg-background-300"
              }`}
              aria-label={`スライド ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}